import { useAcceptInviteMutation } from '@/organizations/apis/org-admin.api'
import { getInvitePreview } from '@/organizations/fetch/get-invite-preview'
import JoinOrganizationModal from '@/organizations/views/JoinOrganizationModal'
import { useContext, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AuthContext } from '@/context/auth-context'
import signOutOfApp from '@/firebase/auth/sign-out'
import { useHubspotConversations } from '@/context/useHubspotConversations'
import FullPageLoader from '@/components/loaders/FullPageLoader'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { GlobalToastType } from '@/constants/constants-ui'
import { useAppDispatch } from '@/store/store-hooks'
import { OrganizationJoinPath } from '@/analytics/enums/organization.enums'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { getOrganizationAnalyticsId, trackOrganizationEvent } from '@/analytics/utils/organization-analytics'
import { getCurrentUserSampleMatterId } from '@/matter/utils/get-current-user-sample-matter-id'
import { Userpilot } from 'userpilot'
import { kDefaultLogoPath } from '@/constants/constants-ui'
import { ApiError } from '@/util/errors/api-error'

type InviteAcceptProps = {
  inviteId: string
  /**
   * True when the user just created a brand-new account to accept this invite. New sign-ups have no
   * existing work to transfer, so they skip the join-confirmation modal and accept directly. Existing
   * accounts (isNewSignUp false) see the modal first to confirm the work-transfer consequences.
   */
  isNewSignUp?: boolean
}

type InviteAcceptStatus = 'idle' | 'previewing' | 'accepting' | 'waiting-for-org' | 'completed' | 'error'

export default function InviteAccept(props: InviteAcceptProps) {
  const { inviteId, isNewSignUp = false } = props
  const { user, userAccountData, refreshUserAccountData } = useContext(AuthContext)
  const navigate = useNavigate()
  const { toggleWidget } = useHubspotConversations()
  const dispatch = useAppDispatch()
  const { trackEvent } = useAnalytics()

  // Inferred values
  const email = user?.email
  const isOrgAccount = userAccountData?.isOrgAccount ?? false
  const orgName = userAccountData?.orgData?.name

  // Local state
  const [error, setError] = useState<string | null>(null)
  const [previewOrgName, setPreviewOrgName] = useState<string | null>(null)
  const [isJoining, setIsJoining] = useState(false)

  const inviteAcceptStatus = useRef<InviteAcceptStatus>('idle')

  // RTK-Query Mutation
  const [acceptInvite] = useAcceptInviteMutation()

  // ======================================================================
  // POLLING NOTE
  // The pending payment poll won't work
  // for existing users who are already paid or trialed
  // who are accepting an invite. So we have a dedicated
  // poll here.
  // ======================================================================

  async function handleAccept() {
    if (inviteAcceptStatus.current === 'accepting') return

    console.log('Handling invitation acceptance...')
    inviteAcceptStatus.current = 'accepting'
    setIsJoining(true)

    try {
      setError(null)
      await acceptInvite({ invite_id: inviteId }).unwrap()
      inviteAcceptStatus.current = 'waiting-for-org'
    } catch (e: unknown) {
      inviteAcceptStatus.current = 'error'
      setIsJoining(false)
      const status = typeof e === 'object' && e != null && 'status' in e ? e.status : undefined

      if (status === 404) {
        setError('404: The invitation could not be found. Try again or contact your organization admin.')
      } else if (status === 410) {
        setError('Your invitation was already completed. Please contact your organization admin.')
      } else {
        setError('There was an error accepting your invitation.')
      }
    }

    // Loading status is intended to continue until polling finds the user in an org
  }

  // Poll for invite status
  // Polls until the user's JWT is updated with the organization_id
  // Then triggers an AuthContext refresh with the latest user data
  useEffect(() => {
    const pollInterval = setInterval(() => {
      console.log('Polling for invite status...')
      // Refresh user account data
      refreshUserAccountData()
      // poll for invite status every 3 seconds
    }, 3000)

    // automatically clear the interval when the component unmounts
    return () => {
      clearInterval(pollInterval)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function fetchPreviewAndShowModal() {
    // Mark the flow as previewing before the request so account polling and the resulting rerenders
    // cannot re-enter the mount effect and fire a second preview fetch while the modal is open.
    inviteAcceptStatus.current = 'previewing'
    try {
      const preview = await getInvitePreview(inviteId)
      setPreviewOrgName(preview.organization_name)
    } catch (e: unknown) {
      inviteAcceptStatus.current = 'error'
      const status = e instanceof ApiError ? e.status : undefined
      if (status === 404) {
        setError('The invitation could not be found. Try again or contact your organization admin.')
      } else if (status === 410) {
        setError('Your invitation was already completed. Please contact your organization admin.')
      } else if (status === 403) {
        setError('This invitation was not sent to your account.')
      } else {
        setError('There was an error loading your invitation.')
      }
    }
  }

  // Starts the accept flow for a user who is not yet in an organization. New sign-ups have no existing
  // work to transfer, so they accept directly; existing accounts confirm via the join modal first.
  function startAcceptFlow() {
    if (isNewSignUp) {
      console.log('New sign-up. Accepting invite directly...')
      handleAccept()
    } else {
      console.log('Existing account. Fetching invite preview to confirm...')
      fetchPreviewAndShowModal()
    }
  }

  // On mount, either accept the invite (new sign-ups) or show the join-confirmation modal (existing
  // accounts), unless the user is already in an organization
  useEffect(() => {
    // only run once, and only when the userAccountData and inviteId have loaded in
    if (inviteAcceptStatus.current === 'idle' && userAccountData && inviteId) {
      // If the user is already in an organization, do not accept the invite, display an error toast and navigate to the home page
      if (isOrgAccount) {
        console.log('User is already in an organization.')
        inviteAcceptStatus.current = 'completed'
        dispatch(openGlobalToast({ type: GlobalToastType.ERROR, message: `You're already a member of ${orgName}.` }))
        navigate('/')
      } else {
        startAcceptFlow()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inviteId, userAccountData])

  // When poll detects the user is in an organization, display a success toast and navigate to the home page
  useEffect(() => {
    if (inviteAcceptStatus.current === 'waiting-for-org' && isOrgAccount) {
      console.log('User has joined the organization. Navigating to home page...')
      inviteAcceptStatus.current = 'completed'

      trackOrganizationEvent(trackEvent, AnalyticsEvent.OrganizationInviteAccepted, {
        organizationId: getOrganizationAnalyticsId(userAccountData),
        organizationInviteId: inviteId,
        organizationJoinPath: isNewSignUp ? OrganizationJoinPath.NewSignUp : OrganizationJoinPath.ExistingAccount,
      })

      async function completeInvite() {
        if (user?.uid) {
          try {
            const sampleMatterId = await getCurrentUserSampleMatterId()

            if (sampleMatterId) {
              Userpilot.identify(user.uid, { sampleMatterId })
              Userpilot.reload()
            } else {
              console.warn('Userpilot sample matter id missing after organization invite acceptance')
            }
          } catch (error) {
            console.error(`Userpilot sample matter id lookup failed after organization invite acceptance for uid ${user.uid}:`, error)
          }
        } else {
          console.error('Userpilot sample matter id sync skipped after organization invite acceptance because user id was missing')
        }

        dispatch(openGlobalToast({ type: GlobalToastType.SUCCESS, message: `Joined ${orgName}.` }))
        navigate('/')
      }

      completeInvite()
    }
  }, [dispatch, inviteId, isNewSignUp, isOrgAccount, navigate, orgName, trackEvent, user?.uid, userAccountData])

  if (!error) {
    return (
      <>
        <FullPageLoader />
        {previewOrgName && (
          <JoinOrganizationModal visible organizationName={previewOrgName} onClose={() => navigate('/')} onConfirm={handleAccept} isJoining={isJoining} />
        )}
      </>
    )
  }

  return (
    <div className={'grow flex flex-col justify-center items-center min-h-screen py-5'}>
      <img className="mx-auto w-[200px] mb-4" src={kDefaultLogoPath} alt="Paxton" />
      <div className="rounded-lg bg-surface-dialog px-4 pb-4 pt-5 mb-5 text-left shadow-xl transition-all sm:w-full sm:max-w-md sm:p-6">
        {(email || orgName) && (
          <div className={'mb-2  text-center'}>
            <span className="font-semibold">{email}</span>
          </div>
        )}
        <div className={'flex flex-col items-center gap-x-2 justify-center'}>
          <div className={'mb-4 text-red-700 text-center'}>{error}</div>
          <button
            className={
              'rounded-md bg-brand-500 border-[1px] border-brand-500 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-brand-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-400'
            }
            onClick={() => {
              setError(null)
              startAcceptFlow()
            }}
          >
            Retry
          </button>
        </div>
      </div>
      <div className={'flex gap-5'}>
        <button
          onClick={() => {
            signOutOfApp(navigate)
          }}
          className="font-semibold leading-6 text-brand-500 hover:text-brand-400"
        >
          Sign out
        </button>
        <button
          onClick={() => {
            toggleWidget()
          }}
          className="font-semibold leading-6 text-brand-500 hover:text-brand-400"
        >
          Chat with support
        </button>
      </div>
    </div>
  )
}
